import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
// Import lazy loading components
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Import product images
import Popye from "../assets/images/popye-bust.png";
import Instagram from "../assets/images/Instagram-scanner.png";
import Gaint from "../assets/images/Gaint.png";
import Flowerblackvase from "../assets/images/Flower-blackvase.png";
import Flowerbluevase from "../assets/images/Flower-bluevase.png";
import Joker from "../assets/images/Joker-bust.png";
import key from "../assets/images/keychain.png";
import Printer from "../assets/images/Printer.jpg";
import moon from "../assets/images/Moonnight.png";
import blasttoise from "../assets/images/Blastoise.png";

// Create a reusable lazy image component
const LazyImage = ({ src, alt, className, placeholderColor = "#e5e7eb" }) => {
  return (
    <LazyLoadImage
      src={src}
      alt={alt}
      effect="blur"
      className={className}
      wrapperClassName={`block ${className}`}
      placeholderSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='${placeholderColor.replace("#", "%23")}'/%3E%3C/svg%3E`}
    />
  );
};

const Home = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState({
    message: "",
    isError: false,
    isSubmitting: false
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({
      message: "Submitting...",
      isError: false,
      isSubmitting: true
    });

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: "6563c1b9-e162-456e-9d1d-b195207c97af",
          name: formData.name,
          email: formData.email,
          message: formData.message,
          botcheck: ""
        })
      });

      const data = await response.json();
      
      if (response.status === 200) {
        setFormStatus({
          message: "Form submitted successfully!",
          isError: false,
          isSubmitting: false
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          message: ""
        });
        // Hide success message after 3 seconds
        setTimeout(() => {
          setFormStatus({
            message: "",
            isError: false,
            isSubmitting: false
          });
        }, 3000);
      } else {
        setFormStatus({
          message: data.message || "Something went wrong. Please try again.",
          isError: true,
          isSubmitting: false
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus({
        message: "Network error. Please try again.",
        isError: true,
        isSubmitting: false
      });
    }
  };

  // Featured services data
  const featuredServices = [
    { id: 1, title: "Standard Printing", image: Joker },
    { id: 2, title: "Rapid Prototyping", image: blasttoise },
    { id: 3, title: "Custom Projects", image: Popye },
    { id: 4, title: "Design Assistance", image: moon },
    { id: 5, title: "High-Resolution Printing", image: Gaint },
    { id: 6, title: "Functional Prototypes", image: Instagram },
    { id: 7, title: "Small Batch Production", image: Flowerblackvase },
    { id: 8, title: "Post-Processing", image: Flowerbluevase },
  ];

  // Featured reviews data
  const featuredReviews = [
    {
      id: 1,
      name: "Archana Jain",
      review: "Good service and best product",
      rating: 5,
      date: "10 April 2025",
      product: "Geometric Sphere",
    },
    {
      id: 2,
      name: "Amit Rawool",
      review: "The product quality is very good, I recommend it to everyone!",
      rating: 5,
      date: "26 March 2025",
      product: "Designer Cube",
    },
    {
      id: 3,
      name: "Shashank Yadav",
      review: "Nice work! will definitely recommend to others 👍",
      rating: 5,
      date: "07 December 2023",
      product: "Abstract Model",
    },
  ];

  // Ref for scrollable services
  const scrollContainerRef = useRef(null);

  // Improved touch handling for auto-scroll functionality
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
  
    const handleInteractionStart = () => {
      scrollContainer.style.animationPlayState = "paused";
    };
  
    const handleInteractionEnd = () => {
      // Add a short delay before resuming animation to ensure the user has stopped interacting
      setTimeout(() => {
        if (scrollContainer) {
          scrollContainer.style.animationPlayState = "running";
        }
      }, 100);
    };
  
    // Mouse events (desktop)
    scrollContainer.addEventListener("mouseenter", handleInteractionStart);
    scrollContainer.addEventListener("mouseleave", handleInteractionEnd);
    
    // Touch events (mobile)
    scrollContainer.addEventListener("touchstart", handleInteractionStart, { passive: true });
    scrollContainer.addEventListener("touchend", handleInteractionEnd);
    scrollContainer.addEventListener("touchcancel", handleInteractionEnd);
  
    return () => {
      // Clean up all event listeners
      scrollContainer.removeEventListener("mouseenter", handleInteractionStart);
      scrollContainer.removeEventListener("mouseleave", handleInteractionEnd);
      scrollContainer.removeEventListener("touchstart", handleInteractionStart);
      scrollContainer.removeEventListener("touchend", handleInteractionEnd);
      scrollContainer.removeEventListener("touchcancel", handleInteractionEnd);
    };
  }, []);

  return (
    <div>
      {/* Hero Section with the animated background */}
      <Hero />

      {/* Featured Services */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              3D Printing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              3D printing offers a world of exciting possibilities for
              individuals and businesses, and 3D PrintTech proudly offers
              high-quality 3D printing services in the local area.
            </p>
          </div>

          {/* Scrollable container with auto-scroll */}
          <div className="relative overflow-hidden w-full">
            <div
              ref={scrollContainerRef}
              className="flex gap-6 animate-scroll cursor-grab active:cursor-grabbing touch-pan-x"
              style={{
                width: "max-content",
              }}
            >
              {/* Duplicate the services array three times for seamless infinite scroll */}
              {[
                ...featuredServices,
                ...featuredServices,
                ...featuredServices,
              ].map((service, index) => (
                <div
                  key={`${service.id}-${index}`}
                  className="flex-shrink-0 w-72 h-96 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl hover:scale-105 overflow-hidden"
                >
                  {/* Replace regular img with LazyImage component */}
                  <LazyImage
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-red-700 to-red-500 hover:from-red-800 hover:to-red-600 text-white font-semibold transition-all duration-300"
            >
              View All Products
            </Link>
          </div>
        </div>

        <style jsx>{`
          /* Animation for auto-scrolling */
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-288px * 8));
            }
          }
          
          .animate-scroll {
            animation: scroll 30s linear infinite;
            -webkit-animation: scroll 30s linear infinite;
            will-change: transform;
            touch-action: pan-x;
          }
          
          /* CSS for improved animation control */
          .animate-scroll {
            -webkit-user-select: none;
            user-select: none;
          }
        `}</style>
      </section>

      {/* Features Section - Original structure with red border on hover using pseudo-element */}
      <section className="py-16 relative overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          {/* Lazy load the background image */}
          <LazyImage
            src={Printer}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-0"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose 3D PrintTech?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              With our specialized equipment, the only limit is your
              imagination. Discover what sets our printing services apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="relative before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-r before:from-red-700 before:to-red-500 before:opacity-0 hover:before:opacity-100 hover:before:-m-[2px] before:transition-all before:duration-300">
              <div className="relative bg-gradient-to-br from-gray-900/70 to-black/70 p-8 rounded-xl border border-gray-800 z-10">
                <div className="w-14 h-14 bg-gradient-to-r from-red-700 to-red-500 rounded-full flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  Superior Print Quality
                </h3>
                <p className="text-gray-400">
                  We use state-of-the-art printers and materials to ensure
                  exceptional detail, durability, and finish in every project.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="relative before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-r before:from-red-700 before:to-red-500 before:opacity-0 hover:before:opacity-100 hover:before:-m-[2px] before:transition-all before:duration-300">
              <div className="relative bg-gradient-to-br from-gray-900/70 to-black/70 p-8 rounded-xl border border-gray-800 z-10">
                <div className="w-14 h-14 bg-gradient-to-r from-red-700 to-red-500 rounded-full flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  Expert Design Support
                </h3>
                <p className="text-gray-400">
                  Our team of experienced designers can help refine your ideas
                  or create custom 3D models to perfectly match your vision.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="relative before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-r before:from-red-700 before:to-red-500 before:opacity-0 hover:before:opacity-100 hover:before:-m-[2px] before:transition-all before:duration-300">
              <div className="relative bg-gradient-to-br from-gray-900/70 to-black/70 p-8 rounded-xl border border-gray-800 z-10">
                <div className="w-14 h-14 bg-gradient-to-r from-red-700 to-red-500 rounded-full flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  Fast Turnaround
                </h3>
                <p className="text-gray-400">
                  We offer rapid prototyping and quick production times,
                  ensuring you get your 3D printed projects when you need them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Read about the experiences of businesses and individuals who have
              used our 3D printing services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredReviews.map((review) => (
              <div
                key={review.id}
                className="bg-white shadow-lg rounded-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-red-700 to-red-500 flex items-center justify-center mr-4 text-white font-bold text-xl">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {review.name}
                    </h3>
                    <div className="flex mt-1">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-5 w-5 ${
                            i < review.rating ? "text-red-500" : "text-gray-300"
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600">{review.review}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/reviews"
              className="inline-block px-8 py-3 rounded-full bg-white border-2 border-red-500 text-red-600 hover:bg-red-50 font-semibold transition-all duration-300"
            >
              View All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-r from-black to-gray-900">
        <div className="absolute inset-0">
          {/* Lazy load the background image */}
          <LazyImage
            src={key}
            alt="Background"
            className="w-full h-full object-cover"
            placeholderColor="#1a1a1a" // Darker placeholder for dark background
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex justify-center">
          <div className="text-center rounded-lg bg-white/10 backdrop-blur-md p-6 shadow-lg inline-block">
            <h2 className="text-3xl md:text-4xl font-bold text-red-500 mb-6">
              Ready to Bring Your Ideas to Life?
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto mb-8">
              Bringing a creative idea to life, making a prototype for a
              potential new business product, creating unique personalized gifts
              – these are just a few of the things you can do with our
              specialized equipment.
            </p>
            <div className="flex justify-center flex-wrap gap-4">
              <Link
                to="/about"
                className="inline-block px-10 py-4 rounded-full bg-red-600 text-white font-bold text-lg hover:bg-red-700 shadow-lg transition-colors"
              >
                know More
              </Link>
              <Link
                to="/services"
                className="inline-block px-10 py-4 rounded-full bg-transparent border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white shadow-lg transition-colors"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section with Web3Forms Integration */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-red-900 to-black relative overflow-hidden">
        {/* Decorative Abstract Shapes */}
        <div className="absolute -top-24 -left-20 w-96 h-96 bg-red-600 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-10 right-0 w-80 h-80 bg-red-500 rounded-full blur-2xl opacity-20 animate-spin-slow"></div>
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-gray-800 rounded-full blur-3xl opacity-30"></div>

        {/* Contact Container */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-lg text-red-200 max-w-2xl mx-auto">
              Have questions about our 3D printing services? We're here to help!
            </p>
          </div>

          {/* Form Box with Web3Forms Integration */}
          <div className="bg-black/40 backdrop-blur-lg border border-red-900/40 rounded-3xl p-10 shadow-2xl transition-transform transform hover:scale-[1.01]">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Hidden input for Web3Forms access key */}
              <input 
                type="hidden" 
                name="access_key" 
                value="6563c1b9-e162-456e-9d1d-b195207c97af"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border-2 border-red-900/50 rounded-xl px-4 py-3 text-white placeholder-red-200/70 focus:outline-none focus:border-red-500 transition-all"
                    placeholder="Your Name"
                    required
                  />
                  <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-red-500 to-red-700 group-focus-within:w-full transition-all duration-300"></div>
                </div>
                
                <div className="relative group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border-2 border-red-900/50 rounded-xl px-4 py-3 text-white placeholder-red-200/70 focus:outline-none focus:border-red-500 transition-all"
                    placeholder="Your Email"
                    required
                  />
                  <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-red-500 to-red-700 group-focus-within:w-full transition-all duration-300"></div>
                </div>
              </div>
              
              <div className="relative group">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full bg-black/30 border-2 border-red-900/50 rounded-xl px-4 py-3 text-white placeholder-red-200/70 focus:outline-none focus:border-red-500 transition-all min-h-[120px]"
                  placeholder="Your Message"
                  required
                ></textarea>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-red-500 to-red-700 group-focus-within:w-full transition-all duration-300"></div>
              </div>
              
              {/* Spam prevention - hidden field */}
              <input 
                type="checkbox" 
                name="botcheck" 
                className="hidden" 
                style={{ display: 'none' }}
              />
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={formStatus.isSubmitting}
                  className="inline-flex items-center px-8 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-800 text-white font-semibold hover:from-red-700 hover:to-red-900 transform hover:scale-105 transition-all duration-200 shadow-lg group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span>
                    {formStatus.isSubmitting ? "Sending..." : "Send Message"}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              
              {/* Form submission status message */}
              {formStatus.message && (
                <div className={`mt-4 text-center p-3 rounded-lg ${
                  formStatus.isError ? 'bg-red-900/50 text-red-200' : 'bg-green-900/50 text-green-200'
                }`}>
                  {formStatus.message}
                </div>
              )}
            </form>
          </div>

          {/* Additional Contact Methods */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="mailto:prathameshlabde2278@gmail.com"
              className="bg-black/30 border border-red-900/30 rounded-2xl p-6 text-center hover:bg-black/40 transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="mx-auto bg-white w-12 h-12 flex items-center justify-center rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Email Us</h3>
              <p className="text-red-200 mt-2 overflow-hidden text-ellipsis whitespace-nowrap">
                prathameshlabde2278@gmail.com
              </p>
            </a>

            <a
              href="tel:+919158235932"
              className="bg-black/30 border border-red-900/30 rounded-2xl p-6 text-center hover:bg-black/40 transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="mx-auto bg-white w-12 h-12 flex items-center justify-center rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Call Us</h3>
              <p className="text-red-200 mt-2">+91 91582 35932</p>
            </a>

            <a
              href="https://www.google.com/maps/place/3D+Print+Tech/@19.4583425,72.8150882,17z/data=!3m1!4b1!4m6!3m5!1s0x3be7af401c97b299:0x6eaa46cc6cae0065!8m2!3d19.4583425!4d72.8150882!16s%2Fg%2F11h_f6rd92?entry=ttu&g_ep=EgoyMDI1MDQyMy4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black/30 border border-red-900/30 rounded-2xl p-6 text-center hover:bg-black/40 transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="mx-auto bg-white w-12 h-12 flex items-center justify-center rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Visit Us</h3>
              <p className="text-red-200 mt-2">A/102 Anusaya CHSL , Virar</p>
            </a>
          </div>
        </div>

        {/* Custom animation class */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin 20s linear infinite;
          }
        `}</style>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;